import React, { useEffect } from 'react';
import { Box, Typography, Select, MenuItem, Stack } from '@mui/material';
import { LocationOn as LocationIcon, KeyboardArrowDown as ArrowIcon } from '@mui/icons-material';
import { useGetMyProfileQuery } from '../../../services/ProfileService';

export interface CenterOption {
  code: string;
  name: string;
}

export interface CenterSelectorProps {
  value: string;
  onChange: (code: string) => void;
  size?: 'small' | 'medium';
  options?: CenterOption[];
  autoSelect?: boolean;
}

const SELECTED_APPEARANCE = {
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  border: '2px solid #cbd5e1',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  hoverBorderColor: '#94a3b8',
  hoverBoxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  iconBackground: '#64748b',
  labelColor: '#64748b',
  label: 'Center',
  arrowColor: '#64748b',
};

const EMPTY_APPEARANCE = {
  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
  border: '2px solid #f59e0b',
  boxShadow: '0 2px 12px rgba(245, 158, 11, 0.25), 0 0 0 3px rgba(245, 158, 11, 0.08)',
  hoverBorderColor: '#d97706',
  hoverBoxShadow: '0 4px 20px rgba(245, 158, 11, 0.35), 0 0 0 4px rgba(245, 158, 11, 0.1)',
  iconBackground: '#f59e0b',
  labelColor: '#92400e',
  label: 'Select Center',
  arrowColor: '#d97706',
};

type CenterAppearance = typeof SELECTED_APPEARANCE;

const getContainerStyles = (appearance: CenterAppearance) => ({
  width: '100%',
  boxSizing: 'border-box',
  background: appearance.background,
  borderRadius: 3,
  px: 2,
  py: 0.75,
  border: appearance.border,
  boxShadow: appearance.boxShadow,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: appearance.hoverBorderColor,
    boxShadow: appearance.hoverBoxShadow,
    transform: 'translateY(-1px)',
  },
});

const getIconStyles = (appearance: CenterAppearance) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: appearance.iconBackground,
  flexShrink: 0,
});

const getLabelStyles = (appearance: CenterAppearance) => ({
  color: appearance.labelColor,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1,
  fontSize: '0.65rem',
  lineHeight: 1,
  display: 'block',
});

const getSelectStyles = (appearance: CenterAppearance) => ({
  width: '100%',
  '& .MuiSelect-select': {
    py: 0,
    pr: '28px !important',
  },
  '& .MuiSvgIcon-root': {
    color: appearance.arrowColor,
    fontSize: 22,
    transition: 'transform 0.2s ease',
  },
  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent',
  },
});

const CENTER_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: 3,
      boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 2px 10px rgba(0,0,0,0.08)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      '& .MuiList-root': {
        py: 0.5,
      },
      '& .MuiMenuItem-root': {
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        mx: 0.75,
        my: 0.25,
        fontSize: '0.9rem',
        fontWeight: 500,
        transition: 'all 0.15s ease',
        '&.Mui-selected': {
          backgroundColor: '#eff6ff',
          color: '#2563eb',
          fontWeight: 700,
          borderLeft: '3px solid #3b82f6',
          '&:hover': { backgroundColor: '#dbeafe' },
        },
        '&:hover': {
          backgroundColor: '#f1f5f9',
          transform: 'translateX(2px)',
        },
      },
    },
  },
};

const CenterValue: React.FC<{ center?: CenterOption }> = ({ center }) => (
  <Typography
    variant="body1"
    sx={{
      fontWeight: 700,
      color: center ? '#1e293b' : '#92400e',
      fontSize: '0.95rem',
      lineHeight: 1.3,
    }}
  >
    {center?.name || 'Choose a center...'}
  </Typography>
);

export const CenterSelector: React.FC<CenterSelectorProps> = ({ value, onChange, size = 'small', options, autoSelect = true }) => {
  const { data: profile } = useGetMyProfileQuery();
  const centers: CenterOption[] = options
    ?? profile?.centerAccess?.map((c: any) => ({ code: c.code, name: c.name }))
    ?? [];

  const selected = centers.find(c => c.code === value);
  const firstCenter = centers[0]?.code;
  useEffect(() => {
    if (!autoSelect || selected) return;
    if (firstCenter) onChange(firstCenter);
  }, [autoSelect, selected, firstCenter, onChange]);
  const appearance = selected ? SELECTED_APPEARANCE : EMPTY_APPEARANCE;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={getContainerStyles(appearance)}
    >
      <Box sx={getIconStyles(appearance)}>
        <LocationIcon sx={{ color: '#ffffff', fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={getLabelStyles(appearance)}>
          {appearance.label}
        </Typography>
        <Select
          size={size}
          value={selected ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          IconComponent={ArrowIcon}
          renderValue={(selectedValue) => (
            <CenterValue center={centers.find(center => center.code === selectedValue)} />
          )}
          variant="standard"
          disableUnderline
          sx={getSelectStyles(appearance)}
          MenuProps={CENTER_MENU_PROPS}
        >
        {centers.map((center) => (
          <MenuItem key={center.code} value={center.code}>
            {center.name}
          </MenuItem>
        ))}
      </Select>
      </Box>
    </Stack>
  );
};
